
import React, { useEffect, useState } from "react";
import "./Report.css";

// REPORT SERVICES
import {
  createReport,
  getMyReports,
} from "../services/reportService";

const ReportContent = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    title: "",
    description: "",
    location: "",
    priority: "Low",
    type: "Pollution",
    image: null,
  });

  const [category, setCategory] = useState("");
  const [preview, setPreview] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  // =====================================================
  // MY REPORTS STATES
  // =====================================================

  const [myReports, setMyReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(true);
  const [reportError, setReportError] = useState("");

  const categories = [
    "Pollution",
    "Infrastructure",
    "Wildlife",
    "Other",
  ];

  // =====================================================
  // FETCH USER REPORTS
  // =====================================================

  const loadMyReports = async () => {
    try {
      setLoadingReports(true);
      setReportError("");

      const response = await getMyReports();

      console.log("========== MY REPORTS ==========");
      console.log(response);
      console.log("===============================");

      // Backend response:
      // { success: true, count: ..., data: [...] }

      if (response?.success) {
        setMyReports(response.data || []);
      } else if (Array.isArray(response)) {
        setMyReports(response);
      } else {
        setMyReports([]);
      }
    } catch (error) {
      console.error("Get My Reports Error:", error);

      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to load your reports.";

      setReportError(message);
    } finally {
      setLoadingReports(false);
    }
  };

  // =====================================================
  // LOAD REPORTS WHEN PAGE OPENS
  // =====================================================

  useEffect(() => {
    loadMyReports();
  }, []);

  // =====================================================
  // INPUT CHANGE
  // =====================================================

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // =====================================================
  // IMAGE
  // =====================================================

  const handleImage = (e) => {
    const file = e.target.files[0];

    if (file) {
      setPreview(URL.createObjectURL(file));

      setFormData({
        ...formData,
        image: file,
      });
    }
  };

  // =====================================================
  // SUBMIT REPORT
  // =====================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    const data = new FormData();

    data.append("name", formData.name);
    data.append("email", formData.email);
    data.append("phone", formData.phone);
    data.append("title", formData.title);
    data.append("description", formData.description);
    data.append("location", formData.location);
    data.append("priority", formData.priority);
    data.append("issueType", formData.type);

    if (formData.image) {
      data.append("image", formData.image);
    }

    try {
      await createReport(data);

      setSubmitted(true);

      // =================================================
      // FORM RESET
      // =================================================

      setFormData({
        name: "",
        email: "",
        phone: "",
        title: "",
        description: "",
        location: "",
        priority: "Low",
        type: "Pollution",
        image: null,
      });

      setCategory("");
      setPreview(null);

      // =================================================
      // IMPORTANT:
      // NEW REPORT WILL APPEAR WITH "pending" STATUS
      // =================================================

      await loadMyReports();

    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Unknown error";

      console.error(
        "Submission Error Details:",
        errorMessage
      );

      alert("Failed to submit: " + errorMessage);
    }
  };

  // =====================================================
  // STATUS DISPLAY
  // =====================================================

  const getStatusClass = (status) => {
    if (!status) return "status-pending";

    const value = status.toLowerCase();

    if (
      value === "resolved" ||
      value === "solved"
    ) {
      return "status-resolved";
    }

    if (
      value === "not solved" ||
      value === "closed unresolved"
    ) {
      return "status-unsolved";
    }

    if (
      value === "in progress" ||
      value === "under review" ||
      value === "ngo review"
    ) {
      return "status-progress";
    }

    if (
      value === "assigned to volunteer" ||
      value === "admin review"
    ) {
      return "status-review";
    }

    return "status-pending";
  };

  // =====================================================
  // STATUS TEXT
  // =====================================================

  const getStatusText = (status) => {
    if (!status) return "Pending";

    switch (status) {
      case "pending":
        return "Pending";

      case "Admin Review":
        return "Admin Review";

      case "Assigned to Volunteer":
        return "Assigned to Volunteer";

      case "In Progress":
        return "In Progress";

      case "Under Review":
        return "Under Review";

      case "Sent to NGO":
        return "Sent to NGO";

      case "Forwarded to NGO":
        return "Forwarded to NGO";

      case "NGO Review":
        return "NGO Review";

      case "Resolved":
        return "Resolved";

      case "Solved":
        return "Solved";

      case "Not Solved":
        return "Not Solved";

      case "Closed Unresolved":
        return "Closed Unresolved";

      default:
        return status;
    }
  };

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <div className="report-page">

      {/* =================================================
          REPORT FORM
      ================================================= */}

      <h1>Submit a Report</h1>

      <p className="subtitle">
        Help us track and restore our planet
      </p>

      {/* CATEGORY CARDS */}

      <div className="category-row">
        {categories.map((cat) => (
          <div
            key={cat}
            className={`category-card ${
              category === cat ? "active" : ""
            }`}
            onClick={() => {
              setCategory(cat);

              setFormData({
                ...formData,
                type: cat,
              });
            }}
          >
            {cat}
          </div>
        ))}
      </div>

      {/* =================================================
          FORM
      ================================================= */}

      <div className="report-layout">

        <div className="form-section">

          <form
            className="form-card"
            onSubmit={handleSubmit}
          >

            {/* NAME + EMAIL */}

            <div className="row">

              <div className="form-group">

                <label>Name</label>

                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  placeholder="Enter your full name"
                  onChange={handleChange}
                  required
                />

              </div>

              <div className="form-group">

                <label>Email</label>

                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  placeholder="e.g., amina@example.com"
                  onChange={handleChange}
                  required
                />

              </div>

            </div>

            {/* PHONE + TITLE */}

            <div className="row">

              <div className="form-group">

                <label>Phone</label>

                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  placeholder="Enter 11-digit number"
                  onChange={handleChange}
                  required
                />

              </div>

              <div className="form-group">

                <label>Report Title</label>

                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  placeholder="Brief title of the issue"
                  onChange={handleChange}
                  required
                />

              </div>

            </div>

            {/* DESCRIPTION */}

            <div className="form-group">

              <label>Description</label>

              <textarea
                name="description"
                value={formData.description}
                placeholder="Provide detailed information about the issue..."
                rows="4"
                onChange={handleChange}
                required
              />

            </div>

            {/* LOCATION */}

            <div className="form-group">

              <label>Location</label>

              <input
                type="text"
                name="location"
                value={formData.location}
                placeholder="Street address or area name"
                onChange={handleChange}
                required
              />

            </div>

            {/* PRIORITY + ISSUE TYPE */}

            <div className="row">

              <div className="form-group">

                <label>Priority</label>

                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                </select>

              </div>

              <div className="form-group">

                <label>Issue Type</label>

                <select
                  name="type"
                  value={formData.type}
                  onChange={(e) => {
                    handleChange(e);
                    setCategory(e.target.value);
                  }}
                >
                  <option value="Pollution">
                    Pollution
                  </option>

                  <option value="Infrastructure">
                    Infrastructure
                  </option>

                  <option value="Wildlife">
                    Wildlife
                  </option>

                  <option value="Other">
                    Other
                  </option>
                </select>

              </div>

            </div>

            {/* IMAGE */}

            <div className="form-group">

              <label>Upload Image</label>

              <input
                type="file"
                onChange={handleImage}
                accept="image/*"
              />

              {preview && (
                <img
                  src={preview}
                  alt="preview"
                  className="preview-img"
                  style={{
                    width: "100px",
                    marginTop: "10px",
                  }}
                />
              )}

            </div>

            {/* SUBMIT */}

            <button
              className="submit-btn"
              type="submit"
            >
              Submit Report
            </button>

            {submitted && (
              <p className="success">
                ✅ Report Submitted Successfully
              </p>
            )}

          </form>

        </div>

      </div>

      {/* =================================================
          MY REPORTS / STATUS SECTION
      ================================================= */}

      <div className="my-reports-section">

        <div className="my-reports-header">

          <div>
            <h2>My Reports</h2>

            <p>
              Track the progress of your submitted reports
            </p>
          </div>

          <button
            type="button"
            className="refresh-reports-btn"
            onClick={loadMyReports}
          >
            🔄 Refresh Status
          </button>

        </div>

        {/* LOADING */}

        {loadingReports && (
          <div className="reports-loading">
            Loading your reports...
          </div>
        )}

        {/* ERROR */}

        {!loadingReports && reportError && (
          <div className="reports-error">
            ❌ {reportError}
          </div>
        )}

        {/* NO REPORTS */}

        {!loadingReports &&
          !reportError &&
          myReports.length === 0 && (
            <div className="no-reports">
              <h3>No Reports Yet</h3>

              <p>
                Your submitted reports will appear here.
              </p>
            </div>
          )}

        {/* REPORT CARDS */}

        {!loadingReports &&
          myReports.length > 0 && (

            <div className="my-reports-grid">

              {myReports.map((report) => {

                const status =
                  report.status || "pending";

                return (
                  <div
                    className="my-report-card"
                    key={report._id}
                  >

                    {/* CARD HEADER */}

                    <div className="report-card-top">

                      <div>

                        <span className="report-label">
                          REPORT
                        </span>

                        <h3>
                          {report.title}
                        </h3>

                      </div>

                      <span
                        className={`report-status ${getStatusClass(
                          status
                        )}`}
                      >
                        {getStatusText(status)}
                      </span>

                    </div>

                    {/* DESCRIPTION */}

                    <p className="report-description">
                      {report.description}
                    </p>

                    {/* REPORT INFO */}

                    <div className="report-info">

                      <div>
                        <strong>
                          Issue:
                        </strong>

                        <span>
                          {report.issueType ||
                            report.type ||
                            "Other"}
                        </span>
                      </div>

                      <div>
                        <strong>
                          Priority:
                        </strong>

                        <span>
                          {report.priority || "Low"}
                        </span>
                      </div>

                      <div>
                        <strong>
                          Location:
                        </strong>

                        <span>
                          {report.location}
                        </span>
                      </div>

                    </div>

                    {/* VOLUNTEER INFO */}

                    {report.assignedVolunteer && (
                      <div className="assigned-info">

                        👤 Assigned Volunteer:{" "}

                        <strong>
                          {report.assignedVolunteer}
                        </strong>

                      </div>
                    )}

                    {/* VOLUNTEER SOLUTION */}

                    {(
                      report.solutionNote ||
                      report.resolutionDetails ||
                      report.whatWasSolved
                    ) && (

                      <div className="solution-info">

                        <strong>
                          Volunteer Update
                        </strong>

                        <p>
                          {report.solutionNote ||
                            report.resolutionDetails ||
                            report.whatWasSolved}
                        </p>

                      </div>

                    )}

                    {/* UNSOLVED REASON */}

                    {(
                      status === "Not Solved" ||
                      status === "Closed Unresolved"
                    ) &&
                      (
                        report.unsolvedReason ||
                        report.notSolvedReason
                      ) && (

                        <div className="unsolved-info">

                          <strong>
                            Reason
                          </strong>

                          <p>
                            {report.unsolvedReason ||
                              report.notSolvedReason}
                          </p>

                        </div>

                      )}

                    {/* LAST UPDATED */}

                    <div className="report-date">

                      {report.updatedAt
                        ? `Last Updated: ${new Date(
                            report.updatedAt
                          ).toLocaleString()}`
                        : report.createdAt
                        ? `Submitted: ${new Date(
                            report.createdAt
                          ).toLocaleString()}`
                        : ""}

                    </div>

                  </div>
                );
              })}

            </div>

          )}

      </div>

    </div>
  );
};

export default ReportContent;